import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Spinner, Tooltip } from "@fluentui/react-components";
import { Edit48Regular } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import IEditPasswordProps from "./IEditPasswordProps";
import PasswordForm from "../form/PasswordForm";
import IPassword from "../../models/password/IPassword";
import passwordService from "../../services/passwordService";
import Status from "../../enums/Status";
import useStyleUtils from "../../utilities/styles";
import Error from "./Error";
import setObjectState from "../../utilities/setObjectState";
import { useTranslation } from "react-i18next";

export default function EditPassword(props: IEditPasswordProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<Status>(Status.NEUTRAL);
    const [form, setForm] = useState<IPassword>(null!);

    const style = useStyleUtils();

    useEffect(() => {
        if (open) {
            getPassword(props.passwordId, setForm, setStatus);
        }
    }, [open, props.passwordId]);
    
    const handleChange = (key: string, value?: string) => setObjectState(setForm, key, value);

    const handleSubmit = () => {
        passwordService.updatePassword(form)
          .then(() => {
            props.onUpdate();
            setOpen(false);
          })
          .catch(error => console.error(error));
      };
    
    return (
        <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip content={t("passwords.titles.edit")} relationship='label'>
                    <Button icon={<Edit48Regular />} />
                </Tooltip>
            </DialogTrigger>
            <DialogSurface>
                <DialogTitle>{t("passwords.titles.edit")}</DialogTitle>
                <DialogBody>
                    <DialogContent>
                        {
                            status === Status.LOADING &&
                            <div className={`${style.textCenter} ${style.w100}`}>
                                <Spinner />
                            </div>
                        }

                        {
                            status === Status.LOAD_ERROR &&
                            <Error />
                        }

                        {
                            status === Status.LOAD_SUCCESS &&
                            <PasswordForm data={form} shouldHidePersonalPassword={true} onChange={handleChange} />
                        }
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary">
                                {t("general.cancel")}
                            </Button>
                        </DialogTrigger>
                        <Button appearance="primary" onClick={handleSubmit}>
                            {t("general.save")}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

async function getPassword(
    passwordId: string,
    setForm: (password: IPassword) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const password = await passwordService.getPassword(passwordId);
        password.password = await passwordService.getPasswordValue(passwordId);

        setForm(password);
        setStatus(Status.LOAD_SUCCESS);
    } catch (error) {
        setStatus(Status.LOAD_ERROR);
    }
}